// components/exercises/lesson/MultipleChoiceExercise.tsx
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { Box, Button, Text, Stack } from "@chakra-ui/react";
import PlayButton from "components/PlayButton";
import {
  ExerciseDefinition,
  ExerciseType,
  MultipleChoiceOption,
} from "utils/exercises/types";
import { useSound } from "providers/SoundProvider";
import VisualPiano from "../VisualPiano";

interface MultipleChoiceExerciseProps {
  exercise: ExerciseDefinition;
  prompt: string;
  choices: MultipleChoiceOption[];
  correctChoice: string;
  onResult: (isCorrect: boolean) => void;
}

const MultipleChoiceExercise = forwardRef(
  (props: MultipleChoiceExerciseProps, ref) => {
    const { exercise, prompt, choices, correctChoice, onResult } = props;

    const [selected, setSelected] = useState<string | null>(null);
    const [answerChecked, setAnswerChecked] = useState(false);
    const { playNote, playChord, isAudioPlaying, setIsAudioPlaying } =
      useSound();

    const handleSelect = (choice: string) => {
      setSelected(choice);
    };

    const localCheckAnswer = () => {
      if (selected !== null) {
        const isCorrect = selected === correctChoice;
        onResult(isCorrect);
        setAnswerChecked(true);
      }
    };

    useImperativeHandle(ref, () => ({
      checkAnswer: () => localCheckAnswer(),
    }));

    const handlePlayNote = () => {
      if (exercise.data.notes) {
        const firstNote = exercise.data.notes[0];
        playNote(firstNote);
      }
    };

    const playInterval = async () => {
      const { notes } = exercise.data;
      if (notes && notes.length === 2) {
        playNote(notes[0]);
        await new Promise((resolve) => setTimeout(resolve, 500)); // Delay between notes
        playNote(notes[1]);
      }
    };

    const playChordNotes = () => {
      const { notes } = exercise.data;
      if (notes && notes.length) {
        playChord(notes);
      }
    };

    const playSound = async () => {
      const randomChance = Math.random();

      if (!isAudioPlaying) {
        setIsAudioPlaying(true);
        switch (exercise.type) {
          case ExerciseType.NOTE_ID:
            return handlePlayNote();
          case ExerciseType.INTERVAL_ID:
            if (randomChance < 0.5) {
              return playInterval(); // Function to play notes sequentially
            } else {
              return playChordNotes(); // Function to play notes simultaneously as a chord
            }
          case ExerciseType.CHORD_ID:
            return playChordNotes();
          default:
            handlePlayNote();
        }

        await new Promise((resolve) => setTimeout(resolve, 3000));
        setIsAudioPlaying(false);
      }
    };

    useEffect(() => {
      setSelected(null);
      setAnswerChecked(false);
      if (!isAudioPlaying) {
        playSound();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [exercise.data.notes]); // Re-run when notes change

    return (
      <Box textAlign="center" width={"100%"}>
        <Text fontSize="2xl" fontWeight={500} mb="4">
          {prompt}
        </Text>
        <PlayButton label="Play Sound" onClick={playSound} />
        <Stack
          spacing={3}
          direction="column"
          align="center"
          mb="4"
          mt={4}
          px={4}
        >
          {choices.map((choice, index) => {
            let backgroundColor = "";
            if (answerChecked) {
              if (choice.label === correctChoice && selected === choice.label) {
                // User selected the correct answer
                backgroundColor = "green.400";
              } else if (
                selected === choice.label &&
                choice.label !== correctChoice
              ) {
                // User selected a wrong answer
                backgroundColor = "red.400";
              } else if (
                choice.label === correctChoice &&
                selected !== choice.label
              ) {
                // Correct answer, but user didn't select it
                //backgroundColor = "green.300";
                backgroundColor = "";
              }
            } else {
              // Not answered yet
              backgroundColor =
                selected === choice.label ? "whiteAlpha.300" : "";
            }

            return (
              <Button
                key={index}
                onClick={() => handleSelect(choice.label)}
                backgroundColor={backgroundColor}
                variant={selected === choice.label ? "solid" : "outline"}
                color={selected === choice.label ? "white" : "whiteAlpha.600"}
                width={"100%"}
                py={6}
                borderRadius={{ base: "xl", md: "lg" }}
                display={"flex"}
                flexDirection={"column"}
                gap={2}
                textAlign="center"
                _hover={{
                  backgroundColor: backgroundColor || "whiteAlpha.200",
                }}
                isDisabled={answerChecked}
                height={"min-content"}
              >
                <Box
                  width={"100%"}
                  display={
                    answerChecked && choice.midiNumbers ? "initial" : "none"
                  }
                >
                  <VisualPiano midiNumbers={choice.midiNumbers || []} />
                </Box>
                <Text>{choice.label}</Text>
              </Button>
            );
          })}
        </Stack>
      </Box>
    );
  }
);

export default MultipleChoiceExercise;
